// PR-P2/P3 — show how every vehicle number is made, edit rates, add other lines.
import { useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Truck, Pencil, FileDown, BarChart3 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/Sheet';
import { apiClient, extractErrorMessage } from '@/lib/api';
import { AddOtherLine } from '@/components/payments/AddOtherLine';
import { openStatementPdf } from '@/lib/statementPdf';

export interface VehicleRow {
  vehicleId: string; plateEn: string | null; code: string | null;
  trips: number; rateSar: number | null; revenueSar: number; driverSalarySar: number;
  maintenanceSar: number; dieselSar: number; otherSar: number; netSar: number;
  investorId: string | null; investorName: string | null; investorPayoutSar: number | null;
}
export type Focus = 'revenue' | 'salary' | 'maint' | 'diesel' | 'net' | 'payout' | 'other';

interface TripLine { id: string; code: string; date: string; site: string | null; qty: string | null; amount: string; driver_pay: string; investor_net: string }
interface Detail {
  range: { from: string; to: string; label: string };
  trips: TripLine[];
  maintenance: Array<{ code: string; date: string; type: string; amount: string }>;
  fuel: Array<{ date: string; liters: string | null; amount: string }>;
  salaryDrivers: Array<{ driverId: string; driverName: string | null; gross: number; tripsOnVehicle: number; tripsTotal: number; allocated: number }>;
  otherLines: Array<{ id: string; date: string; description: string; debit: string; credit: string }>;
}

const sar = (n: number) => n.toLocaleString('en-US', { maximumFractionDigits: 0 });
const sar2 = (s: string | null) => Number(s ?? 0).toLocaleString('en-US', { maximumFractionDigits: 0 });
const pretty = (t: string) => t.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

export function VehicleDetailSheet({
  vehicle, scope, from, to, label, focus, onClose, onReport,
}: {
  vehicle: VehicleRow; scope: string; from: string; to: string; label: string; focus: Focus; onClose: () => void; onReport: () => void;
}) {
  const qc = useQueryClient();
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['vehicle-detail', vehicle.vehicleId, scope, from, to],
    queryFn: async () => (await apiClient.get<Detail>(`/api/v1/project-reports/vehicle-pnl/${vehicle.vehicleId}/detail`, { params: { scope, from, to } })).data,
  });

  const [rateTrip, setRateTrip] = useState<TripLine | null>(null);
  const [newRate, setNewRate] = useState('');
  const [rateBusy, setRateBusy] = useState(false);
  const [rateConfirm, setRateConfirm] = useState<null | { scope: 'trip' | 'vehicle_range'; lockedCount: number; months: string[] }>(null);

  const changed = () => { qc.invalidateQueries({ queryKey: ['vehicle-pnl'] }); qc.invalidateQueries({ queryKey: ['vehicle-detail'] }); refetch(); };

  async function submitRate(rscope: 'trip' | 'vehicle_range', confirmLocked = false) {
    if (!rateTrip || !(Number(newRate) > 0) || !data) return;
    setRateBusy(true);
    try {
      const body: Record<string, unknown> = { scope: rscope, newClientRate: Number(newRate), confirmLocked };
      if (rscope === 'vehicle_range') { body.from = data.range.from; body.to = data.range.to; }
      const res = await apiClient.post<{ needsConfirm?: boolean; lockedCount?: number; lockedMonths?: string[] }>(`/api/v1/trips/${rateTrip.id}/recompute-rate`, body);
      if (res.data?.needsConfirm) { setRateConfirm({ scope: rscope, lockedCount: res.data.lockedCount ?? 0, months: res.data.lockedMonths ?? [] }); return; }
      setRateTrip(null); setNewRate(''); setRateConfirm(null); changed();
    } catch (e) { window.alert(extractErrorMessage(e)); } finally { setRateBusy(false); }
  }

  const hi = (f: Focus) => (focus === f ? 'ring-1 ring-primary/50 bg-primary/5' : '');
  const Section = ({ f, title, value, children }: { f: Focus; title: string; value: string; children?: ReactNode }) => (
    <section className={`rounded-lg border border-border p-3 ${hi(f)}`} data-section={f}>
      <div className="flex items-baseline justify-between">
        <div className="text-xs uppercase tracking-wider text-muted-foreground">{title}</div>
        <div className="font-mono font-medium tabular-nums">{value}</div>
      </div>
      {children && <div className="mt-2 space-y-1 text-xs text-muted-foreground">{children}</div>}
    </section>
  );

  return (
    <Sheet open onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent side="right" className="sm:max-w-lg">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2"><Truck className="h-5 w-5" /> <span className="font-mono" dir="ltr">{vehicle.plateEn ?? vehicle.code}</span> · {label}</SheetTitle>
        </SheetHeader>
        <div className="flex-1 overflow-y-auto p-6 space-y-3" data-vehicle-detail>
          <div className="grid grid-cols-2 gap-2">
            <Button variant="outline" size="sm" className="gap-2" onClick={() => openStatementPdf('vehicle', vehicle.vehicleId, from, to).catch((e) => window.alert(String(e)))}>
              <FileDown className="h-4 w-4" /> Statement PDF
            </Button>
            <Button variant="outline" size="sm" className="gap-2" onClick={onReport}>
              <BarChart3 className="h-4 w-4" /> Report
            </Button>
          </div>
          <div className={`rounded-lg border p-4 ${vehicle.netSar >= 0 ? 'border-emerald-500/40 bg-emerald-500/5' : 'border-rose-500/40 bg-rose-500/5'} ${hi('net')}`}>
            <div className="text-xs uppercase tracking-wider text-muted-foreground">Net</div>
            <div className={`text-2xl font-semibold tabular-nums ${vehicle.netSar >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>{sar(vehicle.netSar)} SAR</div>
            <div className="text-xs text-muted-foreground mt-1 font-mono">
              = revenue {sar(vehicle.revenueSar)} − salary {sar(vehicle.driverSalarySar)} − maint {sar(vehicle.maintenanceSar)} − diesel {sar(vehicle.dieselSar)}{vehicle.otherSar ? ` ${vehicle.otherSar >= 0 ? '+' : '−'} other ${sar(Math.abs(vehicle.otherSar))}` : ''}
            </div>
          </div>

          <Section f="revenue" title="Revenue" value={sar(vehicle.revenueSar)}>
            <div className="font-mono">{vehicle.trips} trips × {vehicle.rateSar != null ? sar(vehicle.rateSar) : '—'} avg = {sar(vehicle.revenueSar)}</div>
            <div className="text-[11px]">Click a trip to fix its rate.</div>
            {isLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : data?.trips?.length ? (
              <div className="max-h-44 overflow-y-auto divide-y divide-border/60 mt-1">
                {data.trips.map((t) => (
                  <button key={t.id} type="button" onClick={() => { setRateTrip(t); setNewRate(''); setRateConfirm(null); }}
                    className="w-full flex justify-between py-1 hover:bg-muted/40 rounded px-1 text-start">
                    <span>{t.date} · {t.site ?? 'trip'} {t.qty ? `(${Number(t.qty)} m³)` : ''}</span>
                    <span className="font-mono inline-flex items-center gap-1">{sar2(t.amount)} <Pencil className="h-3 w-3 opacity-40" /></span>
                  </button>
                ))}
              </div>
            ) : null}

            {rateTrip && (
              <div className="rounded-md border border-primary/40 bg-primary/5 p-2 mt-2 space-y-2" data-rate-editor>
                <div className="text-foreground">Fix rate · trip <span className="font-mono">{rateTrip.code}</span> (now {sar2(rateTrip.amount)})</div>
                <Input type="number" min="0" step="0.01" value={newRate} onChange={(e) => setNewRate(e.target.value)} placeholder="New client rate" className="h-9 text-sm" />
                {rateConfirm ? (
                  <div className="space-y-2">
                    <div className="text-amber-600 dark:text-amber-400">{rateConfirm.lockedCount} trip(s) are in a closed month ({rateConfirm.months.join(', ')}).</div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => setRateConfirm(null)} disabled={rateBusy}>Cancel</Button>
                      <Button size="sm" onClick={() => submitRate(rateConfirm.scope, true)} disabled={rateBusy} className="gap-1">{rateBusy && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Recompute anyway</Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" variant="outline" onClick={() => submitRate('trip')} disabled={rateBusy || !(Number(newRate) > 0)}>This trip only</Button>
                    <Button size="sm" onClick={() => submitRate('vehicle_range')} disabled={rateBusy || !(Number(newRate) > 0)} className="gap-1">{rateBusy && <Loader2 className="h-3.5 w-3.5 animate-spin" />} All {data?.trips.length ?? 0} trips ({data?.range.label})</Button>
                    <Button size="sm" variant="ghost" onClick={() => setRateTrip(null)} disabled={rateBusy}>Close</Button>
                  </div>
                )}
              </div>
            )}
          </Section>

          <Section f="salary" title="Driver salary" value={sar(vehicle.driverSalarySar)}>
            {data?.salaryDrivers?.length ? data.salaryDrivers.map((d) => (
              <div key={d.driverId} className="flex justify-between py-0.5">
                <span>{d.driverName ?? 'driver'} · {d.tripsOnVehicle}/{d.tripsTotal} trips (gross {sar(d.gross)})</span>
                <span className="font-mono">{sar(d.allocated)}</span>
              </div>
            )) : <span>Allocated across the drivers who drove this truck by trip share.</span>}
          </Section>

          <Section f="maint" title="Maintenance" value={sar(vehicle.maintenanceSar)}>
            {isLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : data?.maintenance?.length ? data.maintenance.map((m) => (
              <div key={m.code} className="flex justify-between py-0.5"><span>{m.date} · {pretty(m.type)}</span><span className="font-mono">{sar2(m.amount)}</span></div>
            )) : <span>No maintenance this period.</span>}
          </Section>

          <Section f="diesel" title="Diesel" value={sar(vehicle.dieselSar)}>
            {isLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : data?.fuel?.length ? data.fuel.map((fz, i) => (
              <div key={i} className="flex justify-between py-0.5"><span>{fz.date} · {fz.liters ? `${Number(fz.liters)} L` : 'fuel'}</span><span className="font-mono">{sar2(fz.amount)}</span></div>
            )) : <span>No diesel this period.</span>}
          </Section>

          {/* Other ad-hoc lines on the vehicle. */}
          <Section f="other" title="Other lines" value={vehicle.otherSar ? (vehicle.otherSar >= 0 ? '+' : '−') + sar(Math.abs(vehicle.otherSar)) : '0'}>
            {data?.otherLines?.length ? data.otherLines.map((l) => (
              <div key={l.id} className="flex justify-between py-0.5">
                <span>{l.date} · {l.description}</span>
                <span className={`font-mono ${Number(l.credit) > 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>{Number(l.credit) > 0 ? '+' : '−'}{sar2(Number(l.credit) > 0 ? l.credit : l.debit)}</span>
              </div>
            )) : <span>Nothing extra. Add a one-off cost or credit below.</span>}
            <div className="pt-1"><AddOtherLine subjectKind="vehicle" subjectId={vehicle.vehicleId} onAdded={changed} /></div>
          </Section>

          {vehicle.investorName && (
            <Section f="payout" title={`Investor payout · ${vehicle.investorName}`} value={sar(vehicle.investorPayoutSar ?? 0)}>
              <span>Sum of the investor's net share across this truck's trips (trip-math investor_net). Fix a rate above to change it, or add an adjustment:</span>
              {vehicle.investorId && <div className="pt-1"><AddOtherLine subjectKind="investor" subjectId={vehicle.investorId} onAdded={changed} /></div>}
            </Section>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
